/**
 * "Audit log" under Company (R166, decision 927; help `cloud.audit-log`; docs/specs/cloud/audit-log.md): the
 * company's own recorded changes, Teslyn Support's access and client previews, newest first, for tenant admins and
 * managers. Searched by kind of change, person, words and dates (whole days of the viewer's time, `auditPeriod`);
 * 100 at a time with "Show older". The table is Braynewave's console's (`AuditTable`), with each person's name.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { auditFamilyOptions, auditPeriod } from '@teslyn/domain';
import { AuditTable } from '../admin/AdminAuditScreen.tsx';
import type { AuditRow } from '../admin/api.ts';
import { Button } from '../components/Button.tsx';
import { SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadCompanyAudit, loadCompanyAuditFilters, type AuditFilters } from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const PAGE = 100;

/** The viewer's offset from UTC on a date, in minutes east (the browser's own time zone). */
const offsetOn = (date: string): number => -new Date(`${date}T00:00:00`).getTimezoneOffset();

export interface CompanyAuditScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
}

export function CompanyAuditScreen({ sb, tenantId, tenantName }: CompanyAuditScreenProps): ReactElement {
  const [filters, setFilters] = useState<AuditFilters>({ families: [], people: [] });
  const [family, setFamily] = useState('');
  const [actor, setActor] = useState('');
  const [typed, setTyped] = useState('');
  // the words searched: what was typed, a moment after the typing stops
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCompanyAuditFilters(sb, tenantId)
      .then(setFilters)
      .catch(() => setFilters({ families: [], people: [] }));
  }, [sb, tenantId]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(typed), 400);
    return () => clearTimeout(t);
  }, [typed]);

  const period = auditPeriod(from, to, offsetOn);

  const load = useCallback(
    (before: number | null) => {
      if (period.problem) return;
      setError(null);
      loadCompanyAudit(sb, tenantId, {
        family,
        actor,
        search,
        from: period.from,
        to: period.to,
        before,
        limit: PAGE,
      })
        .then((r) => {
          setRows((old) => (before === null ? r : [...(old ?? []), ...r]));
          setMore(r.length === PAGE);
        })
        .catch((e: unknown) => setError(message(e)));
    },
    [sb, tenantId, family, actor, search, period.from, period.to, period.problem],
  );
  useEffect(() => {
    setRows(null);
    load(null);
  }, [load]);

  const searching = Boolean(family || actor || search.trim() || from || to);

  return (
    <section className="page">
      <PageHeader
        title="Audit log"
        subtitle={`What was changed in ${tenantName}’s Teslyn, by whom and when, newest first: every recorded change, each time Teslyn Support opened or changed something, and each Preview as client.`}
      />
      <div className="form-grid">
        <SelectField
          label="Kind of change"
          value={family}
          onChange={setFamily}
          options={auditFamilyOptions(filters.families)}
        />
        <SelectField
          label="Person"
          value={actor}
          onChange={setActor}
          options={[['', 'Everyone'], ...filters.people.map((p) => [p.id, p.label] as const)]}
        />
        <TextField
          label="Search"
          value={typed}
          onChange={setTyped}
          spellCheck={false}
          hint="Words in what was done or what it was done to, such as a project number or a rego."
        />
        <TextField label="From" type="date" value={from} onChange={setFrom} />
        <TextField label="To" type="date" value={to} onChange={setTo} error={period.problem ?? undefined} />
      </div>
      {searching && (
        <div className="row">
          <Button
            variant="quiet"
            onClick={() => {
              setFamily('');
              setActor('');
              setTyped('');
              setSearch('');
              setFrom('');
              setTo('');
            }}
          >
            Clear the search
          </Button>
        </div>
      )}
      {error && <ErrorState message={error} onRetry={() => load(null)} />}
      {!rows && !error && !period.problem && <Loading what="the audit log" />}
      {rows && rows.length === 0 && searching ? (
        <p className="body muted">Nothing recorded matches the search.</p>
      ) : (
        rows && <AuditTable rows={rows} showTenant={false} />
      )}
      {rows && more && (
        <div className="row">
          <Button onClick={() => load(rows[rows.length - 1]?.id ?? null)}>Show older</Button>
        </div>
      )}
      <p className="caption muted">
        Times are your computer’s. Looking at a page is not recorded, except Teslyn Support’s and a Preview as
        client.
      </p>
    </section>
  );
}
